import { isString } from 'lodash';

import type { TiptapContent, TiptapContentNode } from '../tiptap/tiptapTypes';
import { TiptapNodeType } from '../tiptap/tiptapTypes';
import { mapTiptapNodes } from '../tiptap/utils/jsonContentUtils/mapTiptapNodes';

/**
 * Collects the unique label IDs referenced by task items in a single-editor task list document.
 */
export const getTaskListLabelIds = (document: TiptapContent): string[] => {
    const labelIds = new Set<string>();

    // Find the label IDs within a taskItem node's attributes
    const collectLabelIds = (node: TiptapContentNode) => {
        if (node.type !== TiptapNodeType.taskItem) return;

        const labels = node.attrs?.labels;

        if (!Array.isArray(labels)) return;

        labels.forEach((label) => {
            const labelId = (label as { labelId?: unknown } | null)?.labelId;

            if (!labelId || !isString(labelId)) return;

            labelIds.add(labelId);
        });
    };

    // Recurse over each node
    const visitNode = (node: TiptapContentNode): void => {
        collectLabelIds(node);
        node.content?.forEach(visitNode);
    };

    visitNode(document);
    return [...labelIds];
};

/**
 * Removes a global label reference from every task item in a single-editor task list document.
 */
export const removeTaskListLabelReferences = (document: TiptapContent, labelId: string): TiptapContent =>
    mapTiptapNodes(document, (node) => {
        if (node.type !== TiptapNodeType.taskItem || !Array.isArray(node.attrs?.labels)) return node;

        const labels = node.attrs.labels;
        const updatedLabels = labels.filter((label) => (label as { labelId?: unknown } | null)?.labelId !== labelId);

        if (updatedLabels.length === labels.length) return node;

        return {
            ...node,
            attrs: {
                ...node.attrs,
                labels: updatedLabels,
            },
        };
    }) as TiptapContent;
